import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaSieteM(props, ref) {
  const { content = {}, logoSrc, bgColor } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 900;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // background
      ctx.fillStyle = opts.bgColor || bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // text defaults
      const title = props.title || opts.title || '';
      const subTitle = props.subTitle || opts.subTitle || '';

      // layout
      const padding = 28;
      let y = padding;
      const usableW = width - padding * 2;

      // Title square and title
      const titleFontSize = opts.titleFontSize || 40;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 2);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 12;
      const squareText = opts.titleSquareText || '1.6';

      // draw square at left
      const squareX = padding;
      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(squareX, y, squareSize, squareSize);

      // square number/text
      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, squareX + squareSize / 2, y + squareSize / 2);

      // draw title to the right of square
      const titleX = squareX + squareSize + squareGap;
      if (title) {
        ctx.fillStyle = opts.titleColor || '#0f172a';
        ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        // allow the title to occupy the remaining width to the right of the square
        const titleMaxW = Math.max(0, usableW - (squareSize + squareGap));
        const titleLines = title ? String(title).split('\n') : [];
        const subLines = subTitle ? String(subTitle).split('\n') : [];

        // base sizes
        const baseTitleFontSize = titleFontSize;
        const baseSubSize = opts.subTitleFontSize || 40;

        // line heights (will be adjusted if we scale)
        const baseTitleLineH = Math.round(baseTitleFontSize * 1.2);
        const baseSubLineH = Math.round(baseSubSize);

        // gap between title and subtitle (user-requested 12px)
        const titleSubtitleGap = typeof opts.titleSubtitleGap === 'number' ? opts.titleSubtitleGap : 12;

        // calculate total block height required
        let totalTextH = (titleLines.length ? titleLines.length * baseTitleLineH : 0)
          + (subLines.length ? (titleSubtitleGap + subLines.length * baseSubLineH) : 0);

        // scale down fonts if the combined height exceeds the square
        let titleFontToUse = baseTitleFontSize;
        let subFontToUse = baseSubSize;
        let titleLineH = baseTitleLineH;
        let subLineH = baseSubLineH;
        if (totalTextH > squareSize) {
          const scale = squareSize / totalTextH;
          titleFontToUse = Math.max(10, Math.floor(baseTitleFontSize * scale));
          subFontToUse = Math.max(8, Math.floor(baseSubSize * scale));
          titleLineH = Math.round(titleFontToUse * 1.2);
          subLineH = Math.max(10, Math.round(subFontToUse));
          totalTextH = (titleLines.length ? titleLines.length * titleLineH : 0)
            + (subLines.length ? (titleSubtitleGap + subLines.length * subLineH) : 0);
        }

        // center the title+subtitle block vertically inside the square
        let textStartY = y + Math.round((squareSize - totalTextH) / 2);

        // draw title lines
        if (titleLines.length) {
          ctx.fillStyle = opts.titleColor || '#0f172a';
          ctx.font = `bold ${titleFontToUse}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          let ty = textStartY;
          for (let ln of titleLines) {
            ctx.fillText(ln, titleX, ty);
            ty += titleLineH;
          }
          // draw subtitle immediately after title lines (with the fixed gap)
          if (subLines.length) {
            let sy = textStartY + titleLines.length * titleLineH + titleSubtitleGap;
            ctx.fillStyle = opts.subTitleColor || '#374151';
            ctx.font = `${subFontToUse}px Inter, Arial, sans-serif`;
            ctx.textBaseline = 'top';
            for (let ln of subLines) {
              ctx.fillText(ln, titleX, sy);
              sy += subLineH;
            }
          }
          // advance y to after the square (we keep the square as the bounding box)
          y = y + squareSize;
        } else {
          // no title: still draw subtitle inside square if present
          if (subLines.length) {
            // scale subtitle if necessary
            let subToUse = baseSubSize;
            let subH = baseSubLineH;
            if (subLines.length * subH > squareSize) {
              const s = squareSize / (subLines.length * subH);
              subToUse = Math.max(8, Math.floor(baseSubSize * s));
              subH = Math.max(10, Math.round(subToUse));
            }
            let sy = y + Math.round((squareSize - subLines.length * subH) / 2);
            ctx.fillStyle = opts.subTitleColor || '#374151';
            ctx.font = `${subToUse}px Inter, Arial, sans-serif`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            for (let ln of subLines) {
              ctx.fillText(ln, titleX, sy);
              sy += subH;
            }
          }
          y = y + squareSize;
        }

        // force a fixed 200px gap after the title/square before placing graphics
        y += 200;
      }

      // top gap before charts
      // columns: 3 equal columns with small gap
      const colGap = typeof opts.colGap === 'number' ? opts.colGap : 18;
      const colW = Math.floor((usableW - colGap * 2) / 3);
      const colX0 = padding;
      const colX1 = padding + colW + colGap;
      const colX2 = padding + (colW + colGap) * 2;

      // chart available vertical space (start directly below title/subtitle)
      // enforce a maximum Y coordinate for charts bottom at 600px
      const pageChartsBottom = height - padding - 150; // normal bottom available for charts (room for logo)
      const maxAllowedBottom = 800; // user-requested max y for charts
      const chartsBottom = Math.min(pageChartsBottom, maxAllowedBottom);
      // available vertical space for charts from current y down to chartsBottom
      const availableH = Math.max(0, chartsBottom - y);
      // use availableH directly so charts never extend past the maxAllowedBottom
      const chartsH = availableH;
      const chartsTop = y; // start charts immediately below title/subtitle

      const palette = opts.palette || ['#1644c4', '#06b6d4', '#7678ec', '#9a4cff', '#6366f1', '#a78bfa'];

      const contrastFor = (hex) => {
        try {
          const h = hex.replace('#','');
          const r = parseInt(h.length===3 ? h[0]+h[0] : h.slice(0,2),16);
          const g = parseInt(h.length===3 ? h[1]+h[1] : h.slice(2,4),16);
          const b = parseInt(h.length===3 ? h[2]+h[2] : h.slice(4,6),16);
          const lum = 0.299*r + 0.587*g + 0.114*b;
          return lum < 140 ? '#ffffff' : '#0f172a';
        } catch (e) { return '#ffffff'; }
      };

      // helper: wrap text into lines fitting maxW
      const wrapLines = (c, text, maxW) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = '';
        for (let w of words) {
          const test = cur ? cur + ' ' + w : w;
          if (c.measureText(test).width <= maxW) {
            cur = test;
          } else {
            if (cur) lines.push(cur);
            cur = w;
          }
        }
        if (cur) lines.push(cur);
        return lines;
      };

      // draw pie in a column (cx = column left, maxW, maxH)
      const drawPie = (cx, cyTop, maxW, maxH, items, caption) => {
        const innerPad = 12;
        const captionTopGap = typeof opts.captionTopGap === 'number' ? opts.captionTopGap : 30;

        // prepare caption lines (we need these to reserve vertical space)
        const capSize = opts.captionSize || 20;
        const capLineH = Math.round(capSize * 1.2);
        const capLines = caption ? wrapLines(ctx, caption, maxW - innerPad * 2) : [];

        // reserve vertical space for legend + caption
        const legendRowH = Math.round(opts.legendFontSize || 12) + 4; // per-row height
        const reservedBottom = innerPad * 2 + (items.length * legendRowH) + (capLines.length ? captionTopGap + (capLines.length * capLineH) : 0) + 12;

        const maxDiameter = Math.min(maxW - innerPad * 2, Math.max(40, maxH - reservedBottom));
        const diameter = Math.max(40, Math.min(360, maxDiameter));
        const r = Math.round(diameter / 2);
        const cxCenter = cx + Math.round(maxW / 2);
        const cyCenter = cyTop + r;

        const total = (items || []).reduce((s, it) => s + (Number(it.total) || 0), 0) || 1;

        let ang = -Math.PI / 2;
        for (let i = 0; i < items.length; i++) {
          const it = items[i];
          const v = Number(it.total) || 0;
          const a = (v / total) * Math.PI * 2;
          const start = ang;
          const end = ang + a;
          ctx.beginPath();
          ctx.moveTo(cxCenter, cyCenter);
          const color = palette[i % palette.length];
          ctx.fillStyle = color;
          ctx.arc(cxCenter, cyCenter, r, start, end);
          ctx.lineTo(cxCenter, cyCenter);
          ctx.fill();

          // small label inside if big enough
          const mid = start + a / 2;
          const labelRadius = Math.max(12, Math.round(r * 0.56));
          const lx = cxCenter + Math.cos(mid) * labelRadius;
          const ly = cyCenter + Math.sin(mid) * labelRadius;
          ctx.save();
          ctx.font = `${Math.max(10, Math.round(r * 0.12))}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = contrastFor(color);
          if (a > 0.12) {
            const pct = Math.round((v / total) * 1000) / 10;
            ctx.fillText(`${it.categoria || ''} ${v ? `(${pct}%)` : ''}`, lx, ly);
          } else {
            ctx.beginPath();
            ctx.arc(cxCenter + Math.cos(mid) * (r + 8), cyCenter + Math.sin(mid) * (r + 8), 3, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();

          ang += a;
        }

        // inner hole (optional) - default is full pie (no hole)
        const innerRadius = typeof opts.pieInnerRadius === 'number' ? opts.pieInnerRadius : 0;
        if (innerRadius > 0) {
          ctx.fillStyle = opts.pieInnerBg || '#ffffff';
          ctx.beginPath();
          ctx.arc(cxCenter, cyCenter, Math.round(innerRadius), 0, Math.PI * 2);
          ctx.fill();
        }

        // legend stacked under pie but centered per-row
        ctx.font = `${opts.legendFontSize || 12}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        let ly = cyTop + diameter + 8;
        const legendCenterX = cx + Math.round(maxW / 2);
        for (let i = 0; i < items.length; i++) {
          const it = items[i];
          const cat = it.categoria ? it.categoria.charAt(0).toUpperCase() + it.categoria.slice(1).toLowerCase() : '';
          const label = `${cat} (${it.total || 0})`;
          const textW = ctx.measureText(label).width;
          const rowW = 12 + 6 + textW; // square + gap + text
          const rowStartX = Math.round(legendCenterX - Math.round(rowW / 2));
          const color = palette[i % palette.length];
          ctx.fillStyle = color;
          ctx.fillRect(rowStartX, ly, 12, 12);
          ctx.fillStyle = opts.legendColor || '#111827';
          ctx.fillText(label, rowStartX + 18, ly - 1);
          ly += legendRowH;
        }

        // caption under legend (centered)
        if (capLines.length) {
          ctx.fillStyle = opts.captionColor || '#374151';
          ctx.font = `${capSize}px Inter, Arial, sans-serif`;
          let capY = ly + captionTopGap;
          for (let ln of capLines) {
            ctx.textAlign = 'center';
            ctx.fillText(ln, cx + Math.round(maxW / 2), capY);
            capY += capLineH;
          }
        }
      };

      // draw horizontal comparison bars (left vs right) with legend + caption below
        const drawGroupedBars = (cx, cyTop, maxW, maxH, items, caption) => {
            const innerPad = 12;
            const contentW = Math.max(24, maxW - innerPad * 2);
          const captionTopGap = typeof opts.captionTopGap === 'number' ? opts.captionTopGap : 25;
    
            // caption lines (reserve space)
            const capSize = opts.captionSize || 20;
            const capLineH = Math.round(capSize * 1.2);
            const capLines = caption ? wrapLines(ctx, caption, contentW - innerPad * 2) : [];
    
            // legend row height reserved
            const legendRowH = Math.round(opts.legendFontSize || 12) + 6;
            const reservedBottom = legendRowH + (capLines.length ? captionTopGap + (capLines.length * capLineH) : 0) + 12;
    
            // reserve a left label area so category names don't overlap bars
            const labelArea = Math.max(56, Math.round(contentW * 0.20));
            const chartW = contentW - labelArea;
            const centerX = cx + innerPad + labelArea + Math.round(chartW / 2);
    
            // vertical layout per item
            const n = Math.max(1, items.length);
            const availableH = Math.max(40, maxH - reservedBottom);
            const rowH = Math.max(24, Math.floor(availableH / n));
            const barH = Math.max(8, Math.round(rowH * 0.5));
            const topOffset = cyTop + Math.round((availableH - rowH * n) / 2);
    
            // compute max value for scaling
            const getLeftVal = (it) => Number(it.left ?? it.izquierda ?? it.hombres ?? it.hombre ?? 0);
            const getRightVal = (it) => Number(it.right ?? it.derecha ?? it.mujeres ?? it.mujer ?? 0);
            const maxVal = items.reduce((m, it) => Math.max(m, Math.abs(getLeftVal(it)), Math.abs(getRightVal(it))), 0) || 1;
    
            const halfAvail = Math.max(8, Math.floor((chartW / 2) - 8));
            const leftColor = opts.leftColor || opts.hombresColor || '#06b6d4';
            const rightColor = opts.rightColor || opts.mujeresColor || '#141eaa';
            const leftLabel = opts.leftLabel || 'Hombres';
            const rightLabel = opts.rightLabel || 'Mujeres';
    
            ctx.textBaseline = 'middle';
            ctx.textAlign = 'left';
            ctx.fillStyle = opts.barLabelColor || '#334155';
    
            for (let i = 0; i < n; i++) {
            const it = items[i] || {};
            const rowY = topOffset + i * rowH;
            const valL = Math.max(0, getLeftVal(it));
            const valR = Math.max(0, getRightVal(it));
    
            const wL = Math.round((valL / maxVal) * halfAvail);
            const wR = Math.round((valR / maxVal) * halfAvail);
    
            const barY = rowY + Math.round((rowH - barH) / 2);
    
            // category label in left label area - wrap and justify to up to 2 lines
            const labelText = String((it.categoria || '').replace(/_/g, ' '));
            ctx.textAlign = 'left';
            ctx.fillStyle = opts.barLabelColor || '#334155';
            ctx.font = `${opts.barLabelSize || 11}px Inter, Arial, sans-serif`;
            
            // wrap label to fit available width
            const availableLabelW = labelArea - 8;
            let labelLines = [];
            let line = '';
            const words = labelText.split(/\s+/);
            for (let w of words) {
              const test = line ? line + ' ' + w : w;
              if (ctx.measureText(test).width <= availableLabelW) {
                line = test;
              } else {
                if (line) labelLines.push(line);
                line = w;
              }
            }
            if (line) labelLines.push(line);
            
            // limit to 2 lines max, truncate second line if needed
            if (labelLines.length > 2) {
              labelLines = labelLines.slice(0, 2);
              let truncated = labelLines[1];
              while (ctx.measureText(truncated).width > availableLabelW && truncated.length > 0) truncated = truncated.slice(0, -1);
              labelLines[1] = truncated.length < labelLines[1].length ? truncated.slice(0, -1) + '...' : truncated;
            }
            
            // draw lines centered vertically in the bar area
            const lineH = Math.round(ctx.measureText('M').width * 1.2); // approx line height
            const startY = barY - Math.round((labelLines.length * lineH) / 2);
            for (let i = 0; i < labelLines.length; i++) {
              ctx.fillText(labelLines[i], cx + innerPad + 4, startY + i * lineH);
            }
    
            // left bar (extends left from center)
            ctx.fillStyle = leftColor;
            if (wL > 0) {
                ctx.fillRect(centerX - wL, barY, wL, barH);
            }
    
            // right bar (extends right from center)
            ctx.fillStyle = rightColor;
            if (wR > 0) {
                ctx.fillRect(centerX, barY, wR, barH);
            }
    
            // values: left value to the left of the left bar, right value to the right of the right bar
            ctx.fillStyle = opts.barValueColor || '#0f172a';
            ctx.font = `bold ${Math.max(10, opts.barValueFontSize || 11)}px Inter, Arial, sans-serif`;
    
            ctx.textBaseline = 'middle';
            if (wL > 6) {
                // if bar wide, draw value inside bar near its start (contrast may be needed)
                ctx.textAlign = 'right';
                ctx.fillText(String(valL), centerX - wL - 6, barY + Math.round(barH / 2));
            } else {
                ctx.textAlign = 'right';
                ctx.fillText(String(valL), centerX - wL - 6, barY + Math.round(barH / 2));
            }
    
            ctx.textAlign = 'left';
            ctx.fillText(String(valR), centerX + wR + 6, barY + Math.round(barH / 2));
            }
    
            // legend centered under the chart area
            ctx.font = `${opts.legendFontSize || 12}px Inter, Arial, sans-serif`;
            const textA = leftLabel;
            const textB = rightLabel;
            const wA = ctx.measureText(textA).width;
            const wB = ctx.measureText(textB).width;
            const itemAW = 12 + 6 + wA;
            const itemBW = 12 + 6 + wB;
            const legendGap = 24;
            const totalW = itemAW + legendGap + itemBW;
            const legendStartX = cx + innerPad + labelArea + Math.round((chartW - totalW) / 2);
            const legendY = cyTop + availableH + 8;
    
            // left legend
            ctx.fillStyle = leftColor;
            ctx.fillRect(legendStartX, legendY, 12, 12);
            ctx.fillStyle = opts.legendColor || '#111827';
            ctx.textBaseline = 'top';
            ctx.fillText(textA, legendStartX + 18, legendY - 1);
    
            // right legend
            const secondX = legendStartX + itemAW + legendGap;
            ctx.fillStyle = rightColor;
            ctx.fillRect(secondX, legendY, 12, 12);
            ctx.fillStyle = opts.legendColor || '#111827';
            ctx.fillText(textB, secondX + 18, legendY - 1);
    
            // caption under legend (centered)
            if (capLines.length) {
            ctx.fillStyle = opts.captionColor || '#374151';
            ctx.font = `${capSize}px Inter, Arial, sans-serif`;
            let capY = legendY + legendRowH + captionTopGap;
            for (let ln of capLines) {
                ctx.textAlign = 'center';
                ctx.fillText(ln, cx + Math.round(maxW / 2), capY);
                capY += capLineH;
            }
            }
        };

      // use data items
      const graf1 = props.graficaUnoData || [];
      const graf2 = props.graficaDosData || [];
      const graf3 = props.graficaTresData || [];

      // captions
      const cap1 = props.textoGraficoUno || opts.textoGraficoUno || '';
      const cap2 = props.textoGraficoDos || opts.textoGraficoDos || '';
      const cap3 = props.textoGraficoTres || opts.textoGraficoTres || '';

      // draw left pie
      drawPie(colX0, chartsTop, colW, chartsH, graf1, cap1);

      // draw center grouped bars
      drawGroupedBars(colX1, chartsTop, colW, chartsH, graf2, cap2);

      // draw right pie
      drawPie(colX2, chartsTop, colW, chartsH, graf3, cap3);

      // optional textual qualitative notes under charts (left side) — prefix each item with '-'
      const qual = Array.isArray(props.textoCualitativo) ? props.textoCualitativo : [];
      if (qual.length > 0) {
        let qualX = padding;
        let qualY = chartsTop + chartsH + 120;
        const qualFontSize = opts.qualFontSize || 25;
        ctx.font = `${qualFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.qualColor || '#374151';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const maxW = width - padding * 2;
        const lineHeight = Math.round(qualFontSize * 1.2);

        const dash = '- ';
        const dashW = ctx.measureText(dash).width;

        for (let item of qual) {
          const text = String(item || '');
          // try to wrap taking dash into account so the first line fits with the dash
          const availableW = Math.max(16, maxW - dashW);
          let lines = wrapLines(ctx, text, availableW);

          // fallback if availableW is too small for meaningful wrapping
          if (!lines || lines.length === 0) {
            lines = [''];
          }

          // draw first line with dash prefix
          ctx.fillText(dash + (lines[0] || ''), qualX, qualY);
          qualY += lineHeight;

          // draw subsequent wrapped lines indented to align under text (after dash)
          for (let i = 1; i < lines.length; i++) {
            ctx.fillText(lines[i], qualX + dashW, qualY);
            qualY += lineHeight;
          }

          // small gap between items
          qualY += 10;
        }
      }

      // logo bottom-right (draw then resolve)
      const mainLogoSrc = opts.logoSrc || props.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(240, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_siete_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});